/**
 * The one client an item names, for its logo (Pascal, 30/09/2026 : « ici, il
 * manque les logos des sociétés. et dans le calendrier »). The server gives
 * the raw client name and the same-origin logo route (`client_logo_url`); the
 * frontend never builds a logo URL, and never turns an absence sentence
 * (« client non lié », « société non liée ») into a client name.
 */
import { clientLogoSrc } from '../../components/c/ClientLogo';
import { safeString } from '../../lib/format';

export interface ItemClient {
  name: string;
  /** Same-origin logo route, '' for the initials. */
  logo: string;
}

/**
 * The client of a « Ma semaine » item, from the server's raw `client_name`.
 * `null` when the item has no client. `undefined` when an older server gave
 * only the subtitle: that text then stays plain, since it may be the
 * absence sentence and never a name.
 */
export function myWeekItemClient(item: { client_name?: unknown; client_logo_url?: unknown }): ItemClient | null | undefined {
  if (typeof item.client_name !== 'string') return undefined;
  const name = item.client_name.trim();
  return name ? { name, logo: clientLogoSrc(item) } : null;
}

/**
 * The client of a calendar row: the client of its mission (Fibery session,
 * provisional line), or the company of a Digiforma slot when it has exactly
 * one. A slot shared by several companies gets no logo: one logo would name
 * one of them wrongly.
 */
export function calendarRowClient(row: {
  source_system?: unknown;
  client_name?: unknown;
  client_names?: unknown;
  client_logo_url?: unknown;
}): ItemClient | null {
  const digiforma = safeString(row.source_system).toLowerCase() === 'digiforma';
  let name = '';
  if (digiforma) {
    const names = Array.isArray(row.client_names)
      ? row.client_names.map((value) => safeString(value).trim()).filter(Boolean)
      : [];
    name = names.length === 1 ? names[0] : '';
  } else {
    name = safeString(row.client_name).trim();
  }
  return name ? { name, logo: clientLogoSrc(row) } : null;
}
